import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';
import ImageResize from 'tiptap-extension-resize-image';
import { Box, Paper, Typography, Button } from '@mui/material';
import { PlusCircle } from 'lucide-react';
import EditorToolbar from './EditorToolbar';

interface RichTextEditorProps {
  jsonStringTemplate: string;
  validVariables?: { name: string; variable: string }[];
  saveTemplate: (template: string, jsonTemplate: string) => void;
  value?: string;
  onEditorReady?: (editor: any) => void;
  buttonText?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  jsonStringTemplate,
  validVariables,
  saveTemplate,
  value,
  onEditorReady,
  buttonText = 'Save',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Write something...' }),
      Image.configure({ allowBase64: true }),
      ImageResize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
    ],
    editorProps: {
      attributes: {
        class:
          'prose max-w-none min-h-[220px] [&_ol]:list-decimal [&_ul]:list-disc rounded-lg border border-slate-200 bg-white p-3 text-sm leading-6 focus:outline-none sm:min-h-[260px] sm:p-4',
      },
    },
    content: value ? value : JSON.parse(jsonStringTemplate),
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (editor && variable.length !== 0) {
        const variableText = `{{${variable}}}`;
        editor.chain().focus().insertContent(variableText).run();
      }
    },
    [editor],
  );

  const handleInsertImage = useCallback(
    (src: string) => {
      if (editor) {
        editor.chain().focus().setImage({ src }).run();
      }
    },
    [editor],
  );

  const setLink = useCallback(
    (url: string) => {
      if (!editor) return;
      if (!url) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: `{{${url}}}` })
          .run();
      }
    },
    [editor],
  );

  const exportToHTML = () => {
    if (editor) {
      const html = editor.getHTML();
      const htmlJson = JSON.stringify(editor.getJSON());
      if (jsonStringTemplate !== htmlJson) {
        saveTemplate(html, htmlJson);
      }
    }
  };

  const handleVariableClick = (variableName: string, variable: string) => {
    if (variableName === 'Company Logo') return handleInsertImage(variable);
    if (variableName === 'Assessment Link') return setLink(variable);
    if (variableName === 'Unset Link')
      return editor?.chain().focus().unsetLink().run();
    return handleInsertVariable(variable);
  };

  return (
    <>
      <EditorToolbar editor={editor} />

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 240px' },
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
          <EditorContent editor={editor} />
        </Box>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            bgcolor: 'action.hover',
            p: 2,
            order: { xs: 1, lg: 2 },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
            Insert Variables
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            {validVariables && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: '1fr' },
                  gap: 1,
                }}
              >
                {validVariables.map((variable) => (
                  <Button
                    key={variable.variable}
                    size="small"
                    variant="outlined"
                    startIcon={<PlusCircle size={14} />}
                    onClick={() => handleVariableClick(variable.name, variable.variable)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 4,
                      py: 0.5,
                      px: 1.5,
                      fontSize: '11px',
                      justifyContent: 'flex-start',
                      width: '100%',
                    }}
                  >
                    {variable.name}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', width: '100%', justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', pt: 2 }}>
        <Button
          variant="contained"
          onClick={exportToHTML}
          sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
        >
          {buttonText}
        </Button>
      </Box>
    </>
  );
};

export default RichTextEditor;
